import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  checkIsAdmin,
  getEmailLog,
  getContactSubmissions,
  listPortfolio,
  upsertPortfolio,
  deletePortfolio,
  getSettings,
  updateSetting,
  getSystemHealth,
} from "@/lib/admin.functions";
import { BlogView } from "@/components/admin/blog-view";

export const Route = createFileRoute("/_authenticated/admin")({
  component: AdminPage,
  head: () => ({
    meta: [
      { title: "Admin — PIXIWEB" },
      { name: "description", content: "Internes Admin-Dashboard von PIXIWEB zur Verwaltung von Portfolio, Kontaktanfragen, E-Mails und Systemeinstellungen." },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Admin — PIXIWEB" },
      { property: "og:description", content: "Internes Admin-Dashboard von PIXIWEB zur Verwaltung von Portfolio, Kontaktanfragen, E-Mails und Systemeinstellungen." },
      { property: "og:url", content: "https://pixiweb.de/admin" },
    ],
    links: [{ rel: "canonical", href: "https://pixiweb.de/admin" }],
  }),
});

type EmailRow = {
  id: string;
  message_id: string | null;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  created_at: string;
};

type ContactRow = {
  id: string;
  created_at: string;
  name: string;
  email: string;
  budget: string | null;
  message: string;
};

type PortfolioRow = {
  id: string;
  title: string;
  tag: string;
  image_url: string;
  external_url: string;
  sort_order: number;
  is_visible: boolean;
};

const RANGES = { "24h": 1, "7d": 7, "30d": 30, all: null } as const;
type RangeKey = keyof typeof RANGES;
type Tab = "emails" | "contacts" | "portfolio" | "blog" | "settings" | "health";

function AdminPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const adminCheck = useServerFn(checkIsAdmin);
  const fetchEmails = useServerFn(getEmailLog);
  const fetchContacts = useServerFn(getContactSubmissions);

  const adminQuery = useQuery({ queryKey: ["isAdmin"], queryFn: () => adminCheck() });

  const emailsQuery = useQuery({
    queryKey: ["admin", "emails"],
    queryFn: () => fetchEmails(),
    enabled: !!adminQuery.data?.isAdmin,
  });
  const contactsQuery = useQuery({
    queryKey: ["admin", "contacts"],
    queryFn: () => fetchContacts(),
    enabled: !!adminQuery.data?.isAdmin,
  });

  const [range, setRange] = useState<RangeKey>("7d");
  const [template, setTemplate] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [tab, setTab] = useState<Tab>("emails");

  const allEmails: EmailRow[] = emailsQuery.data?.rows ?? [];

  const filteredEmails = useMemo(() => {
    const days = RANGES[range];
    const cutoff = days ? Date.now() - days * 24 * 60 * 60 * 1000 : 0;
    return allEmails.filter((r) => {
      if (cutoff && new Date(r.created_at).getTime() < cutoff) return false;
      if (template !== "all" && r.template_name !== template) return false;
      if (status !== "all") {
        if (status === "sent" && r.status !== "sent") return false;
        if (status === "failed" && r.status !== "dlq" && r.status !== "failed") return false;
        if (status === "suppressed" && r.status !== "suppressed") return false;
        if (status === "pending" && r.status !== "pending") return false;
      }
      return true;
    });
  }, [allEmails, range, template, status]);

  const templates = useMemo(() => {
    const s = new Set(allEmails.map((r) => r.template_name));
    return Array.from(s).sort();
  }, [allEmails]);

  const stats = useMemo(() => {
    const total = filteredEmails.length;
    let sent = 0, failed = 0, suppressed = 0, pending = 0;
    for (const r of filteredEmails) {
      if (r.status === "sent") sent++;
      else if (r.status === "dlq" || r.status === "failed" || r.status === "bounced") failed++;
      else if (r.status === "suppressed") suppressed++;
      else if (r.status === "pending") pending++;
    }
    return { total, sent, failed, suppressed, pending };
  }, [filteredEmails]);

  async function handleSignOut() {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  if (adminQuery.isLoading) {
    return <div className="p-10 text-center text-slate">Lade…</div>;
  }

  if (adminQuery.data && !adminQuery.data.isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ice px-4">
        <div className="max-w-md rounded-2xl border border-mist bg-white p-8 text-center shadow-pixi">
          <h1 className="font-display text-2xl font-bold text-navy">Kein Zugriff</h1>
          <p className="mt-2 text-sm text-slate">Dieser Account hat keine Admin-Rechte.</p>
          <button
            onClick={handleSignOut}
            className="mt-6 rounded-xl bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-electric"
          >
            Abmelden
          </button>
        </div>
      </div>
    );
  }

  const navItems: { id: Tab; label: string }[] = [
    { id: "emails", label: "E-Mail-Protokoll" },
    { id: "contacts", label: "Kontaktanfragen" },
    { id: "portfolio", label: "Portfolio" },
    { id: "blog", label: "Ratgeber" },
    { id: "settings", label: "Einstellungen" },
    { id: "health", label: "System-Health" },
  ];

  return (
    <div className="flex min-h-screen bg-ice">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-mist bg-white md:flex">
        <div className="border-b border-mist px-5 py-5">
          <Link to="/" className="font-display text-lg font-bold text-navy">
            PIXIWEB <span className="text-electric">Admin</span>
          </Link>
        </div>
        <nav className="flex flex-1 flex-col gap-1 p-3 text-sm">
          {navItems.map((n) => (
            <button
              key={n.id}
              onClick={() => setTab(n.id)}
              className={`rounded-lg px-3 py-2 text-left ${tab === n.id ? "bg-navy text-white" : "text-slate hover:bg-ice hover:text-navy"}`}
            >
              {n.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-mist p-3">
          <button
            onClick={handleSignOut}
            className="w-full rounded-lg border border-mist px-3 py-2 text-sm text-navy hover:bg-ice"
          >
            Abmelden
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-2 border-b border-mist bg-white px-4 py-3 md:hidden">
          <Link to="/" className="font-display text-base font-bold text-navy">
            PIXIWEB <span className="text-electric">Admin</span>
          </Link>
          <select
            value={tab}
            onChange={(e) => setTab(e.target.value as Tab)}
            className="rounded-md border border-mist bg-white px-2 py-1 text-xs"
          >
            {navItems.map((n) => (
              <option key={n.id} value={n.id}>{n.label}</option>
            ))}
          </select>
          <button
            onClick={handleSignOut}
            className="rounded-md border border-mist px-2.5 py-1 text-xs text-navy"
          >
            Abmelden
          </button>
        </header>

        <main className="flex-1 px-4 py-6 md:px-6 md:py-8">
          {tab === "emails" && (
            <EmailsView
              stats={stats}
              range={range}
              setRange={setRange}
              template={template}
              setTemplate={setTemplate}
              templates={templates}
              status={status}
              setStatus={setStatus}
              rows={filteredEmails}
              loading={emailsQuery.isLoading}
              onRefresh={() => emailsQuery.refetch()}
            />
          )}
          {tab === "contacts" && (
            <ContactsView
              rows={(contactsQuery.data?.rows ?? []) as ContactRow[]}
              loading={contactsQuery.isLoading}
              onRefresh={() => contactsQuery.refetch()}
            />
          )}
          {tab === "portfolio" && <PortfolioView />}
          {tab === "blog" && <BlogView />}
          {tab === "settings" && <SettingsView />}
          {tab === "health" && <HealthView />}
        </main>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number | string; tone: string }) {
  return (
    <div className="rounded-xl border border-mist bg-white p-4">
      <div className="text-xs uppercase tracking-wide text-slate">{label}</div>
      <div className={`mt-1 font-display text-2xl font-bold ${tone}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    sent: "bg-green-100 text-green-800",
    dlq: "bg-red-100 text-red-800",
    failed: "bg-red-100 text-red-800",
    bounced: "bg-red-100 text-red-800",
    suppressed: "bg-yellow-100 text-yellow-800",
    pending: "bg-blue-100 text-blue-800",
    complained: "bg-orange-100 text-orange-800",
  };
  const cls = map[status] ?? "bg-gray-100 text-gray-800";
  return <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${cls}`}>{status}</span>;
}

function EmailsView(props: {
  stats: { total: number; sent: number; failed: number; suppressed: number; pending: number };
  range: RangeKey;
  setRange: (r: RangeKey) => void;
  template: string;
  setTemplate: (t: string) => void;
  templates: string[];
  status: string;
  setStatus: (s: string) => void;
  rows: EmailRow[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const { stats, range, setRange, template, setTemplate, templates, status, setStatus, rows, loading, onRefresh } = props;
  const [page, setPage] = useState(0);
  const pageSize = 50;
  const paged = rows.slice(page * pageSize, (page + 1) * pageSize);
  const pages = Math.max(1, Math.ceil(rows.length / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold text-navy">E-Mail-Protokoll</h2>
        <button onClick={onRefresh} className="rounded-lg border border-mist bg-white px-3 py-1.5 text-sm text-navy hover:bg-ice">
          Aktualisieren
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <StatCard label="Gesamt" value={stats.total} tone="text-navy" />
        <StatCard label="Gesendet" value={stats.sent} tone="text-green-700" />
        <StatCard label="Fehler" value={stats.failed} tone="text-red-700" />
        <StatCard label="Geblockt" value={stats.suppressed} tone="text-yellow-700" />
        <StatCard label="Wartend" value={stats.pending} tone="text-blue-700" />
      </div>

      <div className="flex flex-wrap gap-3 rounded-xl border border-mist bg-white p-4">
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate">Zeitraum:</span>
          {(["24h", "7d", "30d", "all"] as RangeKey[]).map((r) => (
            <button key={r} onClick={() => setRange(r)} className={`rounded-md px-2.5 py-1 text-xs ${range === r ? "bg-navy text-white" : "border border-mist text-slate hover:text-navy"}`}>
              {r === "24h" ? "24h" : r === "7d" ? "7 Tage" : r === "30d" ? "30 Tage" : "Alle"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate">Vorlage:</span>
          <select value={template} onChange={(e) => setTemplate(e.target.value)} className="rounded-md border border-mist bg-white px-2 py-1 text-xs">
            <option value="all">Alle</option>
            {templates.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate">Status:</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-mist bg-white px-2 py-1 text-xs">
            <option value="all">Alle</option>
            <option value="sent">Gesendet</option>
            <option value="failed">Fehler</option>
            <option value="suppressed">Geblockt</option>
            <option value="pending">Wartend</option>
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-mist bg-white">
        <table className="w-full text-sm">
          <thead className="bg-ice">
            <tr className="text-left text-xs uppercase text-slate">
              <th className="px-4 py-3">Vorlage</th>
              <th className="px-4 py-3">Empfänger</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Zeitpunkt</th>
              <th className="px-4 py-3">Fehler</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-6 text-center text-slate">Lade…</td></tr>
            ) : paged.length === 0 ? (
              <tr><td colSpan={5} className="p-6 text-center text-slate">Keine E-Mails im gewählten Zeitraum.</td></tr>
            ) : (
              paged.map((r) => (
                <tr key={r.id} className="border-t border-mist">
                  <td className="px-4 py-3 font-medium text-navy">{r.template_name}</td>
                  <td className="px-4 py-3 text-slate">{r.recipient_email}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 text-slate">{new Date(r.created_at).toLocaleString("de-DE")}</td>
                  <td className="px-4 py-3 text-xs text-red-700">{r.error_message ?? ""}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {pages > 1 && (
          <div className="flex items-center justify-between border-t border-mist px-4 py-3 text-xs text-slate">
            <span>Seite {page + 1} von {pages} · {rows.length} Einträge</span>
            <div className="flex gap-2">
              <button disabled={page === 0} onClick={() => setPage(page - 1)} className="rounded-md border border-mist px-2 py-1 disabled:opacity-50">Zurück</button>
              <button disabled={page >= pages - 1} onClick={() => setPage(page + 1)} className="rounded-md border border-mist px-2 py-1 disabled:opacity-50">Weiter</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ContactsView({ rows, loading, onRefresh }: { rows: ContactRow[]; loading: boolean; onRefresh: () => void }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold text-navy">Kontaktanfragen</h2>
        <button onClick={onRefresh} className="rounded-lg border border-mist bg-white px-3 py-1.5 text-sm text-navy hover:bg-ice">Aktualisieren</button>
      </div>
      <div className="space-y-3">
        {loading ? (
          <p className="text-center text-slate">Lade…</p>
        ) : rows.length === 0 ? (
          <p className="rounded-xl border border-mist bg-white p-6 text-center text-slate">Noch keine Anfragen.</p>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-mist bg-white p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <div className="font-semibold text-navy">{r.name}</div>
                  <a href={`mailto:${r.email}`} className="text-sm text-electric hover:underline">{r.email}</a>
                </div>
                <div className="text-xs text-slate">{new Date(r.created_at).toLocaleString("de-DE")}</div>
              </div>
              {r.budget && <div className="mt-2 text-xs text-slate">Budget: <span className="font-medium text-navy">{r.budget}</span></div>}
              <p className="mt-3 whitespace-pre-wrap text-sm text-navy/80">{r.message}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ===== Portfolio =====

const EMPTY_PORTFOLIO: PortfolioRow = {
  id: "",
  title: "",
  tag: "Website",
  image_url: "",
  external_url: "",
  sort_order: 0,
  is_visible: true,
};

function PortfolioView() {
  const qc = useQueryClient();
  const fetchList = useServerFn(listPortfolio);
  const upsertFn = useServerFn(upsertPortfolio);
  const deleteFn = useServerFn(deletePortfolio);

  const query = useQuery({ queryKey: ["admin", "portfolio"], queryFn: () => fetchList() });
  const rows: PortfolioRow[] = (query.data?.rows ?? []) as PortfolioRow[];

  const [editing, setEditing] = useState<PortfolioRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutSave = useMutation({
    mutationFn: (data: PortfolioRow) =>
      upsertFn({
        data: {
          id: data.id || null,
          title: data.title,
          tag: data.tag,
          image_url: data.image_url,
          external_url: data.external_url,
          sort_order: data.sort_order,
          is_visible: data.is_visible,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "portfolio"] });
      setEditing(null);
      setError(null);
    },
    onError: (e: any) => setError(e?.message ?? "Speichern fehlgeschlagen"),
  });

  const mutDelete = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "portfolio"] }),
    onError: (e: any) => setError(e?.message ?? "Löschen fehlgeschlagen"),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-2xl font-bold text-navy">Portfolio</h2>
        <div className="flex gap-2">
          <button onClick={() => query.refetch()} className="rounded-lg border border-mist bg-white px-3 py-1.5 text-sm text-navy hover:bg-ice">Aktualisieren</button>
          <button
            onClick={() => { setError(null); setEditing({ ...EMPTY_PORTFOLIO, sort_order: (rows.at(-1)?.sort_order ?? 0) + 10 }); }}
            className="rounded-lg bg-navy px-3 py-1.5 text-sm font-semibold text-white hover:bg-electric"
          >
            + Neues Projekt
          </button>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}

      <div className="overflow-hidden rounded-xl border border-mist bg-white">
        <table className="w-full text-sm">
          <thead className="bg-ice">
            <tr className="text-left text-xs uppercase text-slate">
              <th className="px-3 py-3">Sort</th>
              <th className="px-3 py-3">Bild</th>
              <th className="px-3 py-3">Titel</th>
              <th className="px-3 py-3">Tag</th>
              <th className="px-3 py-3">Link</th>
              <th className="px-3 py-3">Sichtbar</th>
              <th className="px-3 py-3 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading ? (
              <tr><td colSpan={6} className="p-6 text-center text-slate">Lade…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="p-6 text-center text-slate">Noch keine Einträge.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-t border-mist">
                  <td className="px-3 py-3 text-slate">{r.sort_order}</td>
                  <td className="px-3 py-3">
                    {r.image_url ? (
                      <img src={r.image_url} alt="" className="h-10 w-16 rounded object-cover" />
                    ) : (
                      <span className="inline-block h-10 w-16 rounded bg-mist" />
                    )}
                  </td>
                  <td className="px-3 py-3 font-medium text-navy">{r.title}</td>
                  <td className="px-3 py-3"><span className="rounded-md bg-electric/10 px-2 py-0.5 text-xs font-medium text-electric">{r.tag}</span></td>
                  <td className="px-3 py-3">
                    {r.external_url ? (
                      <a href={r.external_url} target="_blank" rel="noopener noreferrer" className="text-xs text-electric hover:underline">Öffnen ↗</a>
                    ) : (
                      <span className="text-xs text-slate/60">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {r.is_visible ? <span className="text-green-700">●</span> : <span className="text-slate/60">○</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button onClick={() => { setError(null); setEditing(r); }} className="rounded-md border border-mist px-2 py-1 text-xs text-navy hover:bg-ice">Bearbeiten</button>
                    <button
                      onClick={() => { if (confirm(`„${r.title}" wirklich löschen?`)) mutDelete.mutate(r.id); }}
                      className="ml-2 rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <PortfolioEditor
          initial={editing}
          saving={mutSave.isPending}
          onCancel={() => { setEditing(null); setError(null); }}
          onSave={(data) => mutSave.mutate(data)}
        />
      )}
    </div>
  );
}

function PortfolioEditor({
  initial,
  saving,
  onCancel,
  onSave,
}: {
  initial: PortfolioRow;
  saving: boolean;
  onCancel: () => void;
  onSave: (data: PortfolioRow) => void;
}) {
  const [form, setForm] = useState<PortfolioRow>(initial);
  useEffect(() => setForm(initial), [initial]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-pixi">
        <h3 className="font-display text-xl font-bold text-navy">
          {form.id ? "Projekt bearbeiten" : "Neues Projekt"}
        </h3>
        <div className="mt-4 space-y-3 text-sm">
          <label className="block">
            <span className="text-slate">Titel</span>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 w-full rounded-lg border border-mist px-3 py-2"
              placeholder="z. B. ModernBedacht GmbH"
            />
          </label>
          <label className="block">
            <span className="text-slate">Kategorie-Tag</span>
            <input
              value={form.tag}
              onChange={(e) => setForm({ ...form, tag: e.target.value })}
              className="mt-1 w-full rounded-lg border border-mist px-3 py-2"
              placeholder="Website / E-Commerce / SEO …"
            />
          </label>
          <label className="block">
            <span className="text-slate">Bild-URL</span>
            <input
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              className="mt-1 w-full rounded-lg border border-mist px-3 py-2"
              placeholder="https://…/screenshot.jpg"
            />
            {form.image_url && (
              <img src={form.image_url} alt="" className="mt-2 h-32 w-full rounded-lg object-cover" />
            )}
          </label>
          <label className="block">
            <span className="text-slate">Projekt-URL (externer Link)</span>
            <input
              value={form.external_url}
              onChange={(e) => setForm({ ...form, external_url: e.target.value })}
              className="mt-1 w-full rounded-lg border border-mist px-3 py-2"
              placeholder="https://beispiel.de/"
            />
          </label>
          <div className="flex items-center gap-4">
            <label className="block flex-1">
              <span className="text-slate">Reihenfolge</span>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "0", 10) })}
                className="mt-1 w-full rounded-lg border border-mist px-3 py-2"
              />
            </label>
            <label className="flex cursor-pointer items-center gap-2 pt-5 text-slate">
              <input
                type="checkbox"
                checked={form.is_visible}
                onChange={(e) => setForm({ ...form, is_visible: e.target.checked })}
              />
              Sichtbar
            </label>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-lg border border-mist px-4 py-2 text-sm text-navy hover:bg-ice">Abbrechen</button>
          <button
            onClick={() => onSave(form)}
            disabled={saving || !form.title.trim() || !form.tag.trim()}
            className="rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-electric disabled:opacity-50"
          >
            {saving ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== Settings =====

function SettingsView() {
  const qc = useQueryClient();
  const fetchSettings = useServerFn(getSettings);
  const saveFn = useServerFn(updateSetting);

  const query = useQuery({ queryKey: ["admin", "settings"], queryFn: () => fetchSettings() });
  const [email, setEmail] = useState("");
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (query.data) setEmail(query.data.settings.contact_recipient_email);
  }, [query.data]);

  const mut = useMutation({
    mutationFn: (value: string) => saveFn({ data: { key: "contact_recipient_email", value } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "settings"] });
      setSavedAt(Date.now());
      setError(null);
    },
    onError: (e: any) => setError(e?.message ?? "Fehler beim Speichern"),
  });

  return (
    <div className="space-y-6">
      <h2 className="font-display text-2xl font-bold text-navy">Einstellungen</h2>

      <div className="max-w-lg rounded-xl border border-mist bg-white p-6">
        <h3 className="font-display text-lg font-semibold text-navy">Kontaktformular</h3>
        <p className="mt-1 text-sm text-slate">
          E-Mail-Adresse, an die Benachrichtigungen über neue Kontaktanfragen gesendet werden.
        </p>
        <label className="mt-4 block text-sm">
          <span className="text-slate">Empfänger-Adresse</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-lg border border-mist px-3 py-2"
            placeholder="kontakt@pixiweb.de"
          />
        </label>
        {error && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={() => mut.mutate(email.trim())}
            disabled={mut.isPending || !email.trim()}
            className="rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-electric disabled:opacity-50"
          >
            {mut.isPending ? "Speichern…" : "Speichern"}
          </button>
          {savedAt && <span className="text-xs text-green-700">Gespeichert ✓</span>}
        </div>
      </div>
    </div>
  );
}

// ===== System Health =====

function HealthView() {
  const fetchHealth = useServerFn(getSystemHealth);
  const query = useQuery({
    queryKey: ["admin", "health"],
    queryFn: () => fetchHealth(),
    refetchInterval: 30_000,
  });

  const d = query.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold text-navy">System-Health</h2>
        <button onClick={() => query.refetch()} className="rounded-lg border border-mist bg-white px-3 py-1.5 text-sm text-navy hover:bg-ice">
          Aktualisieren
        </button>
      </div>

      {query.isLoading ? (
        <p className="text-slate">Lade…</p>
      ) : !d ? (
        <p className="text-slate">Keine Daten verfügbar.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="E-Mails 24h" value={d.emailsLast24h} tone="text-navy" />
            <StatCard label="Fehler (DLQ, 7T)" value={d.dlqLast7d} tone={d.dlqLast7d > 0 ? "text-red-700" : "text-green-700"} />
            <StatCard label="Geblockt gesamt" value={d.suppressedTotal} tone="text-yellow-700" />
            <StatCard label="Anfragen 24h" value={d.contactsLast24h} tone="text-electric" />
          </div>

          <div className="rounded-xl border border-mist bg-white p-5">
            <h3 className="font-display text-base font-semibold text-navy">Letzte E-Mail-Aktivität</h3>
            {d.latestEmail ? (
              <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                <div><span className="text-slate">Zeitpunkt:</span> <span className="text-navy">{new Date(d.latestEmail.created_at).toLocaleString("de-DE")}</span></div>
                <div><span className="text-slate">Status:</span> <StatusBadge status={d.latestEmail.status} /></div>
                <div><span className="text-slate">Vorlage:</span> <span className="text-navy">{d.latestEmail.template_name}</span></div>
                <div className="truncate"><span className="text-slate">Empfänger:</span> <span className="text-navy">{d.latestEmail.recipient_email}</span></div>
                {d.latestEmail.error_message && (
                  <div className="sm:col-span-2 text-red-700"><span className="text-slate">Fehler:</span> {d.latestEmail.error_message}</div>
                )}
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate">Noch keine E-Mails versendet.</p>
            )}
          </div>

          <p className="text-xs text-slate">
            Stand: {new Date(d.checkedAt).toLocaleString("de-DE")} · Aktualisiert sich automatisch alle 30 s.
          </p>
        </>
      )}
    </div>
  );
}
